import { Box } from '@mui/system';
import Stack from '@mui/material/Stack';
import { Card, CardHeader, CardContent } from '@mui/material';

import { useTranslate } from 'src/locales';
import { EntityLabel } from 'src/services/utils/utils.types';

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import EmptyContent from 'src/components/empty-content';

type Props = {
  labels?: EntityLabel[];
  companyName?: string | null;
};

/**
 * Read-only "Labels" box on the contact profile (JT1-1541 QA). Contacts carry no labels of their own —
 * these are inherited from the contact's linked company, so the card is display-only (labels are edited
 * on the company profile). Rendered only when the contact has a linked company.
 */
export function ContactInheritedLabelsCard({ labels, companyName }: Props) {
  const { t } = useTranslate();

  if (!companyName) return null;

  const hasLabels = !!labels?.length;

  return (
    <Card>
      <CardHeader
        title={
          <Stack direction="row" gap={1} alignItems="center">
            <Iconify icon="tabler:tags" />
            <Box>{t('contacts.profile.labels.title', { defaultValue: 'Labels' })}</Box>
          </Stack>
        }
        subheader={t('contacts.profile.labels.inherited_from', {
          defaultValue: 'Inherited from {{company}}',
          company: companyName,
        })}
        subheaderTypographyProps={{ variant: 'caption' }}
      />
      <CardContent>
        {hasLabels ? (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {labels!.map((label) => (
              <Label key={label.id} variant="soft">
                {label.label}
              </Label>
            ))}
          </Stack>
        ) : (
          <EmptyContent
            title={t('contacts.profile.labels.empty', { defaultValue: 'No labels on the linked company' })}
            sx={{ py: 2 }}
          />
        )}
      </CardContent>
    </Card>
  );
}
